import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, CheckCircle, ChevronRight } from 'lucide-react';
import { Project } from '../../types';
import { useNavigate } from 'react-router-dom';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate();

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const percentageUsed = (project.spent / project.budget) * 100;
  const isOverBudget = project.budgetStatus === 'over';

  const handleClick = () => {
    navigate(`/projects/${project.projectId}`);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'completed':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'completed':
        return 'Completado';
      default:
        return 'Suspendido';
    }
  };

  return (
    <Card
      onClick={handleClick}
      className="hover:shadow-lg transition-shadow cursor-pointer"
    >
      <CardContent className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {project.name}
            </h3>
            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          </div>
          {project.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {project.description}
            </p>
          )}
        </div>

        {/* Budget Information */}
        <div className="space-y-3 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Presupuesto:</span>
            <span className="font-medium">
              {formatCurrency(project.budget)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Gastado:</span>
            <span className="font-medium">
              {formatCurrency(project.spent)}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progreso</span>
            <span>{Math.min(percentageUsed, 100).toFixed(1)}%</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${
                isOverBudget
                  ? 'bg-red-600'
                  : percentageUsed > 80
                  ? 'bg-yellow-500'
                  : 'bg-green-600'
              }`}
              style={{ width: `${Math.min(percentageUsed, 100)}%` }}
            />
          </div>
        </div>

        {/* Available/Over Budget Indicator */}
        <div
          className={`p-4 rounded-lg ${
            isOverBudget ? 'bg-red-50' : 'bg-green-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-xs font-medium ${
                  isOverBudget ? 'text-red-800' : 'text-green-800'
                }`}
              >
                {isOverBudget ? 'Sobre presupuesto' : 'Disponible'}
              </p>
              <p
                className={`text-2xl font-bold ${
                  isOverBudget ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {formatCurrency(Math.abs(project.available))}
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isOverBudget ? 'bg-red-100' : 'bg-green-100'
              }`}
            >
              {isOverBudget ? (
                <AlertTriangle className="w-6 h-6 text-red-600" />
              ) : (
                <CheckCircle className="w-6 h-6 text-green-600" />
              )}
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4">
          <Badge variant={getStatusVariant(project.status)}>
            {getStatusLabel(project.status)}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
